import {cdnImage} from '@/util/cdnImage';
import {WHITE_LABEL_CRYPTO_DATA} from '@/util/metaData/crypto';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import React, {useEffect, useState} from 'react';
import styles from '@/styles/whiteLabelCryptoWallet.module.scss';

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        adminWhiteLabelCheck,
        businessGrowthCheck,
        dominateDevelopmentService,
        makeOurWhiteLabelContent,
        multiRevenueWallet,
        ourMakeWhiteLabelChoice,
        partnershipWhiteLabelData,
        userWhiteLabelCheck,
        whiteCryptoFAQ,
        whiteLabelBannerPoints,
        whiteLabelClone,
        whiteLabelDecodingData,
        whiteLabelIndustry,
        whiteLabelProcess,
        whiteLabelServiceContent,
        whiteLabelTestmonialData
    } = await import('@/util/contents/white-crypto-data');

    const {
        bannerBottomCryptoWalletData,
        whiteLabelBannerAwards,
        whiteLabelMobileAwards,
        whiteLabelMobilePartnership,
        whiteLabelPartnershipData
    } = await import('@/util/contents/white-label-exchange');

    return {
        props: {
            adminWhiteLabelCheck,
            businessGrowthCheck,
            dominateDevelopmentService,
            makeOurWhiteLabelContent,
            multiRevenueWallet,
            ourMakeWhiteLabelChoice,
            partnershipWhiteLabelData,
            userWhiteLabelCheck,
            whiteCryptoFAQ,
            whiteLabelBannerPoints,
            whiteLabelClone,
            whiteLabelDecodingData,
            whiteLabelIndustry,
            whiteLabelProcess,
            whiteLabelServiceContent,
            whiteLabelTestmonialData,
            bannerBottomCryptoWalletData,
            whiteLabelBannerAwards,
            whiteLabelMobileAwards,
            whiteLabelMobilePartnership,
            whiteLabelPartnershipData
        }
    };
};

const Decoding = dynamic(() => import('@/components/Ui-components/Decoding'));
import {MUIButton, MUIContainer} from '@/components/MUI-Components';
import {spacing} from '@/styles/tokens';
const Platforms = dynamic(() => import('@/components/Ui-components/platforms'));
const DynamicScreens = dynamic(() => import('@/components/Ui-components/Dynamic-screens'));
const OneStepSolution = dynamic(() => import('@/components/Ui-components/one-step-solution'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));
const Questions = dynamic(() => import('@/components/organisms/accordions/Questions'));
const whiteLabelBusiness = cdnImage('crypto-images/White-Label-wallet-overview.webp');
const solution = cdnImage('crypto-images/White-Label-Crypto-Wallet-Solution.webp');
const adminDashboard = cdnImage('crypto-images/Admin-dashboard.webp');
const seamLess = cdnImage('crypto-images/User-dashboard.webp');
const businessImg = cdnImage('crypto-images/Scalable-Wallet.webp');
const customWalletNiche = cdnImage('crypto-images/custom-crypto-wallet-for-niche.webp');
const customize = cdnImage('crypto-images/Customized-white-label-crypto-wallet.webp');
const cryptoIndustry = cdnImage('crypto-images/crypto-industry.webp');
const walletCustom = cdnImage('crypto-images/Custom-crypto-wallet.webp');
const development = cdnImage('crypto-images/White-Label-Crypto-Wallet-Process.svg');
import ServiceTab from '@/components/Ui-components/ServiceTab';
import Link from 'next/link';
const YouTubeEmbed = dynamic(() => import('@/components/Common/YouTubeEmbed'), {ssr: false, loading: () => null});
const watchLive = cdnImage('crypto-images/watch-live-demo.webp');
const LetsDiscuss = dynamic(() => import('@/components/Ui-components/Lets-discuss'));
const watchButton = cdnImage('crypto-images/Live-demo-button.webp');
const OurMakeChoice = dynamic(() => import('@/components/Ui-components/OurMakeChoice'));
import AggregateScript from '@/components/Common/Aggregate-script';
import {meetingLink} from '@/constants';
import {CDN_URL, SITE_URL} from '@/constants/urls';

// Above-the-fold sections are imported statically, not with next/dynamic: a dynamic section renders
// nothing until its chunk arrives after an in-site navigation, then pushes the page down (CLS).
import Banner from '@/components/Ui-components/Banner';
import DisplayImage from '@/components/Ui-components/display-image';

const MobileProcess = dynamic(() => import('@/components/Ui-components/MobileProcess'));
// Migrated from globals.scss `.white-label-crypto-main-parent .banner-img-outer` (nested override on the shared Banner component)
const whiteLabelCryptoMainParentSx = {
    '& .banner-img-outer': {display: 'flex !important', alignItems: 'center !important'}
} as const;

// Migrated from globals.scss `.white-label-crypto-main-parent .sub-banner-wrapper`
const subBannerWrapperSx = {padding: '20px 0 !important'} as const;

// Migrated from globals.scss `.white-crypto-decoding-img`
const whiteCryptoDecodingImgSx = {maxWidth: '500px'} as const;

// Migrated from globals.scss `.crypto-header-video-img`
const cryptoHeaderVideoImgSx = {
    width: '150px !important',
    height: '150px !important',
    '@media (max-width: 430px)': {width: '100px !important', height: '100px !important'}
} as const;

// Migrated from globals.scss `.white-label-crypto-one-step .spectrum-header-wrapper`
const whiteLabelCryptoOneStepSx = {
    '& .spectrum-header-wrapper': {marginBottom: `${spacing[60]} !important`}
} as const;

// Migrated from globals.scss `.white-video-text-container` (sole consumer — this page
// only; identical duplicate rule block also removable at globals.scss:4162).
const whiteVideoTextContainerSx = {
    textAlign: 'left !important',
    maxWidth: '515px !important', // no exact token match
    '& .spectrum-heading': {lineHeight: '50px !important'}, // no exact token match
    '@media (max-width: 768px)': {maxWidth: '100% !important'}
} as const;

// Migrated from globals.scss `.video-text-grid` (sole consumer — this page only;
// identical duplicate rule block also removable at globals.scss:4166).
const videoTextGridSx = {
    alignSelf: 'center !important',
    paddingRight: `${spacing[8]} !important`,
    '@media (max-width: 768px)': {
        paddingRight: 'unset !important',
        marginBottom: '30px !important' // no exact token match
    }
} as const;

// Migrated from globals.scss `.hire-partnerShip-wrapper .helper-card-icon`
// (sole consumer — this page only).
const hirePartnershipWrapperSx = {
    '& .helper-card-icon': {width: `${spacing[60]} !important`}
} as const;

// Migrated from globals.scss `.react-player-show-img` (sole consumer — this page only).
const reactPlayerShowImgSx = {
    width: '100% !important',
    height: 'auto !important',
    maxWidth: '550px !important' // no exact token match
} as const;

// Migrated from globals.scss `.react-watch-button` (sole consumer — this page only;
// identical duplicate rule block also removable at globals.scss:4654).
const reactWatchButtonSx = {
    width: `${spacing[60]} !important`,
    height: `${spacing[60]} !important`,
    '@media (max-width: 430px)': {
        width: `${spacing[44]} !important`,
        height: `${spacing[44]} !important`
    }
} as const;

// Migrated from globals.scss `.react-player-inside-img` (sole consumer — this page only;
// identical duplicate rule block also removable at globals.scss:4658).
const reactPlayerInsideImgSx = {
    top: '45px !important', // no exact token match
    left: '45px !important', // no exact token match
    '@media (max-width: 430px)': {
        top: `${spacing[28]} !important`,
        left: `${spacing[28]} !important`
    }
} as const;

// Migrated from globals.scss `.react-player-video-wrapper` (sole consumer — this page
// only; identical duplicate rule blocks also removable at globals.scss:4650, 4928).
const reactPlayerVideoWrapperSx = {
    top: '210px !important', // no exact token match
    left: '38% !important',
    cursor: 'pointer',
    '@media (max-width: 430px)': {
        top: '130px !important', // no exact token match
        left: '33% !important'
    },
    '@media (max-width: 320px)': {
        top: '100px !important' // no exact token match
    }
} as const;

const WhiteLabelWallet = (props: any) => {
    const {
        openForm,
        adminWhiteLabelCheck,
        businessGrowthCheck,
        dominateDevelopmentService,
        makeOurWhiteLabelContent,
        multiRevenueWallet,
        ourMakeWhiteLabelChoice,
        partnershipWhiteLabelData,
        userWhiteLabelCheck,
        whiteCryptoFAQ,
        whiteLabelBannerPoints,
        whiteLabelClone,
        whiteLabelDecodingData,
        whiteLabelIndustry,
        whiteLabelProcess,
        whiteLabelServiceContent,
        whiteLabelTestmonialData,
        bannerBottomCryptoWalletData,
        whiteLabelBannerAwards,
        whiteLabelMobileAwards,
        whiteLabelMobilePartnership,
        whiteLabelPartnershipData
    } = props;
    const [startVideo, setStartVideo] = useState(false);
    const mobileScreen = useMediaQuery('(max-width:480px)');
    const [dataToRender, setDataToRender] = useState<any>([]);
    const adminDashboardSubheading = [
        `New to managing a crypto wallet? No sweat. We get it—running a platform can seem like a lot, but our all-in-one admin dashboard makes it simple. Manage deposits, withdrawals, and track wallet performance with ease. Everything’s just a click away.
`
    ];

    const userDashboardSubHeading = [
        'With Debut Infotech’s white label digital wallet solution, users can easily store, send, and receive cryptocurrencies without the fuss of complex setups or linking to third-party apps. Enjoy fast, secure payments and easy management of crypto holdings with zero hassle.'
    ];

    const businessGrowth = [
        'Give your users a secure, reliable wallet that they can trust. Our platform comes with advanced features designed to ensure financial stability, while maintaining strong compliance and simplifying legal processes.'
    ];

    useEffect(() => {
        if (mobileScreen) {
            setDataToRender(whiteLabelMobilePartnership);
        } else {
            setDataToRender(whiteLabelPartnershipData);
        }
    }, [mobileScreen, whiteLabelMobilePartnership, whiteLabelPartnershipData]);

    return (
        <Box sx={whiteLabelCryptoMainParentSx}>
            <PageHead meta={WHITE_LABEL_CRYPTO_DATA} url="/white-label-crypto-wallet" />
            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="White Label Crypto Wallet"
                subheading="Roll out your custom-branded crypto wallet with our ready-to-deploy white label crypto wallet solution. Fully customizable to suit your business needs, this solution is designed to get you operational quickly and efficiently."
                image={cdnImage('crypto-images/white-label-crypto-wallet.webp')}
                // bannerBackground="white-label-exchange-banner-background"
                alt="White Label Crypto Wallet"
                DataPoints={whiteLabelBannerPoints}
                button="Request a Free Demo!"
                bannerIcons={mobileScreen ? whiteLabelMobileAwards : whiteLabelBannerAwards}
            />
            <Box className="sub-banner-wrapper" sx={subBannerWrapperSx}>
                <MUIContainer>
                    <Grid container spacing={2} alignItems="center" className="white-label-bottom-container">
                        <Grid item md={2} lg={1.7} sm="auto" xs={12}>
                            <Typography className="banner-bottom-heading">Key Partners</Typography>
                        </Grid>
                        {bannerBottomCryptoWalletData?.map((data: any, index: number) => (
                            <Grid item md={2} lg={1.7} sm="auto" xs={6} className="sub-banner-images" key={index + 1}>
                                <Box className="images-banner-bottom">
                                    <DisplayImage src={data.src} alt={data.alt} priority={true} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MUIContainer>
            </Box>
            <Grid item xs={12} className="bottom-margin top-space decording-main-wrapper mobile-top-padding mobile-bottom-margin">
                <MUIContainer>
                    <Decoding
                        imageSrc={whiteLabelBusiness}
                        alt="Make A Million Dollar Business With Our White Label Crypto Wallet"
                        heading="Make A Million Dollar Business With Our White Label Crypto Wallet"
                        subHeading={whiteLabelDecodingData}
                        imgSx={whiteCryptoDecodingImgSx}
                    />
                </MUIContainer>
            </Grid>
            <hr className="hr-line" />
            <Box className="bottom-spacing margin mobile-top-margin mobile-bottom-padding">
                <MUIContainer>
                    <Grid container>
                        <Grid item xs={12} sm={12} md={6} lg={6} className="video-text-grid" sx={videoTextGridSx}>
                            <Box className="spectrum-header-wrapper white-video-text-container" sx={whiteVideoTextContainerSx}>
                                <Typography className="spectrum-heading color-black">
                                    A Quick Look at Our White Label Crypto Wallet Solution
                                </Typography>
                                <Typography className="spectrum-sub-heading color-black" mt={2}>
                                    Launch your custom crypto wallet with our cutting-edge white label digital wallet solution. Built for
                                    the future of digital finance, Carteira supports multi-chain, multi-currency functionality while
                                    offering a sleek, user-friendly experience and top-notch security.
                                </Typography>
                                <Typography className="spectrum-sub-heading color-black">
                                    Our white label crypto wallet development services make it easy to integrate your brand into a
                                    high-performing platform. As a leader in the space, we provide free white label wallet consultations, so
                                    you get exactly what you need to stay ahead of the competition. Get the{' '}
                                    <a target="_blank" href="/blog/unlocking-the-future-the-ultimate-guide-to-the-best-crypto-wallets">
                                        best crypto wallets
                                    </a>{' '}
                                    solution that’s designed to grow with your business and keep you in the game.
                                </Typography>

                                {/* <Typography className="white-label-book-now" variant="h6">
                                    Launch Your Own Custom Crypto Wallet in 6 Weeks
                                </Typography> */}
                                <Link href="https://calendly.com/debut" className="menu-links" target="_blank">
                                    <MUIButton
                                        label="Book Free Demo!"
                                        variant="contained"
                                        className="helper-card-button"
                                        aria-label="Book Free Demo!"
                                    />
                                </Link>
                            </Box>
                        </Grid>
                        <Grid xs={12} sm={12} md={6} lg={6} item textAlign="center">
                            {startVideo ? (
                                <YouTubeEmbed
                                    url="https://www.youtube.com/watch?v=fcL3BQZwY-s&embeds_referring_euri=http%3A%2F%2F182.75.105.186%3A3087%2F&source_ve_path=Mjg2NjY&feature=emb_logo"
                                    className={`white-label-video ${styles.whiteLabelVideo}`}
                                    controls
                                    playing={startVideo}
                                />
                            ) : (
                                <Box position="relative">
                                    <DisplayImage
                                        src={solution}
                                        priority={false}
                                        className="react-player-show-img"
                                        sx={reactPlayerShowImgSx}
                                        alt="A Quick Look at Our White Label Crypto Wallet Solution"
                                    />
                                    <Box
                                        position="absolute"
                                        className="react-player-video-wrapper"
                                        sx={reactPlayerVideoWrapperSx}
                                        onClick={() => setStartVideo(!startVideo)}
                                    >
                                        <Box position="relative">
                                            <DisplayImage
                                                src={watchLive}
                                                className={`spin-animation ${styles.spinAnimation}`}
                                                sx={cryptoHeaderVideoImgSx}
                                                alt="watch live"
                                                priority={false}
                                            />

                                            <Box position="absolute" className="react-player-inside-img" sx={reactPlayerInsideImgSx}>
                                                <DisplayImage
                                                    src={watchButton}
                                                    priority={false}
                                                    className="react-watch-button"
                                                    sx={reactWatchButtonSx}
                                                    alt="play button"
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Box>
            <Box className="launch-demo-video-wrapper" py={2.3}>
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-sub-heading color-white text-left" component="h2">
                            Launch Your Own Custom Crypto Wallet in 6 Weeks
                        </Typography>
                    </Box>
                </MUIContainer>
            </Box>
            <Grid item xs={12} className="top-space bottom-spacing helper-section helper_section_parent mobile-top-padding">
                <MUIContainer>
                    <ServiceTab
                        heading="Must-Have Features of Our White Label Cryptocurrency Wallet"
                        subheading="As a top player in white label cryptocurrency wallet development, we arm businesses with all the must-have features that match today’s market trends. From top-notch security to high liquidity and full customization, our platform lets you outshine the competition and grab market share fast. Plus, we’ll level up your crypto wallet app by packing it with cutting-edge features that boost performance and keep you ahead in the game."
                        technology={whiteLabelServiceContent}
                    />
                </MUIContainer>
            </Grid>
            <Grid item xs={12}>
                <Box className="renowed-client-wrapper top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper  solutions">
                            <Typography className="spectrum-heading color-black ">
                                Reasons Why Investing in Our Crypto Wallets Pays Off
                            </Typography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line" />
                            </Box>
                            <Typography className="spectrum-sub-heading color-black spacing">
                                Investing in our white label{' '}
                                <a target="_blank" href="/eWallet-app-development">
                                    digital wallet solutions
                                </a>{' '}
                                isn’t just about tech—it’s about giving your business the edge it needs. We’ve built a system that’s
                                reliable, secure, and ready to keep you ahead in the fast-paced digital world.
                            </Typography>
                            <Grid
                                container
                                className="our-reowned-icon-wrapper defi-reowned-icon-wrapper top-spacing hire-partnerShip-wrapper"
                                sx={hirePartnershipWrapperSx}
                                spacing={3}
                            >
                                {partnershipWhiteLabelData?.map((data: any, index: number) => (
                                    <Grid item key={index + 1} md={3} sm={4} xs={6}>
                                        <DisplayImage
                                            src={data.src}
                                            priority={false}
                                            alt={data.alt}
                                            className="helper-card-icon"
                                            width={100}
                                            height={100}
                                        />
                                        <Typography variant="body2" component="p" className="our-reowned-remote-heading">
                                            {data.heading}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </MUIContainer>
                </Box>
            </Grid>
            <hr className="hr-line zero-margin" />

            <Grid item xs={12} className="dynmaic-white-label-wrapper top-space mobile-top-padding ">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black " component="h2">
                            Get a Look at Our White Label Crypto Wallet Interface{' '}
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line" />
                        </Box>
                    </Box>
                    <DynamicScreens
                        imageSrc={adminDashboard}
                        subHeading="All-in-One Admin Dashboard"
                        heading="Effortlessly Manage Every Part of Your Crypto Wallet"
                        subPoints={adminDashboardSubheading}
                        reverseOrder={true}
                        alt="All-in-One Admin Dashboard"
                        buttonLabel="Request a Demo"
                        buttonClass="nft-button"
                        checkItems={adminWhiteLabelCheck}
                        openForm={() => {
                            globalThis.open(meetingLink, '_blank');
                        }}
                    />
                    <DynamicScreens
                        imageSrc={seamLess}
                        subHeading="Seamless User Dashboard"
                        heading="Give Your Customers a Wallet They’ll Love"
                        subPoints={userDashboardSubHeading}
                        reverseOrder={false}
                        alt="Seamless User Dashboard"
                        buttonLabel="Request a Demo"
                        buttonClass="helper-card-button"
                        checkItems={userWhiteLabelCheck}
                        openForm={() => {
                            globalThis.open(meetingLink, '_blank');
                        }}
                    />
                    <DynamicScreens
                        imageSrc={businessImg}
                        subHeading="Scalable Wallet Features for Business Growth"
                        heading="Power Up Your Business with Customizable Wallet Solutions"
                        subPoints={businessGrowth}
                        reverseOrder={true}
                        alt="Scalable Wallet Features for Business Growth"
                        buttonLabel="Request a Demo"
                        buttonClass="nft-button"
                        checkItems={businessGrowthCheck}
                        openForm={() => {
                            globalThis.open(meetingLink, '_blank');
                        }}
                    />
                </MUIContainer>
            </Grid>
            <Grid item xs={12}>
                <Box className="renowed-client-wrapper bottom-spacing mobile-bottom-padding">
                    <MUIContainer>
                        <Box className="spectrum-header-wrapper margin solutions mobile-top-margin">
                            <Typography className="spectrum-heading top-space color-black ">Our Renowned Clients</Typography>
                            <Box className="divder-wrapper">
                                <hr className="divider-line" />
                            </Box>
                            <Grid container className="our-reowned-icon-wrapper">
                                {dataToRender?.map((data: any, index: number) => (
                                    <Grid item key={index + 1} md={3} sm={6} xs={12}>
                                        <DisplayImage
                                            src={data.src}
                                            alt={data.alt}
                                            priority={false}
                                            className="helper-card-icon"
                                            width={100}
                                            height={100}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </MUIContainer>
                </Box>
            </Grid>
            <Grid item xs={12} className="hire-native-team rich-exchange-feature-adv  exchange-banner-white-label launch-defi-wrapper">
                <MUIContainer>
                    <Grid container justifyContent="space-around">
                        <Grid item xs={12} sm={12} md={8} className="defi-short-banner-center">
                            <Box className="spectrum-header-wrapper solution-header-wrapper solutions zero-margin">
                                <Typography className="spectrum-heading defi-adv-heading ">
                                    See How Our Customized Solutions Have Made a Difference in the Crypto Industry.{' '}
                                </Typography>

                                <Box py={1}>
                                    <MUIButton
                                        className="nft-button"
                                        variant="contained"
                                        label="Download Our Case Studies"
                                        onClick={openForm}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={4} md={4} className="native-img right">
                            <DisplayImage src={walletCustom} alt="customized solutions have made a difference" width={230} />
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Grid>
            <Box className="top-space mobile-top-padding" sx={whiteLabelCryptoOneStepSx}>
                <MUIContainer className="bottom-margin mobile-bottom-margin">
                    <OneStepSolution
                        OneStepData={multiRevenueWallet}
                        sectionhead="Unlock Multiple Revenue Streams with Our White Label Crypto Wallet"
                        className="white-label-crypto-card"
                    />
                </MUIContainer>
            </Box>
            <Grid item xs={12} className="hire-native-team rich-exchange-feature-adv  exchange-banner-white-label launch-defi-wrapper">
                <MUIContainer>
                    <Grid container justifyContent="space-around">
                        <Grid item xs={12} sm={12} md={7} className="defi-short-banner-center">
                            <Box className="spectrum-header-wrapper solution-header-wrapper solutions zero-margin">
                                <Typography className="spectrum-heading defi-adv-heading ">
                                    Scale Your Earnings with Our White Label Crypto Wallet Solution
                                </Typography>

                                <Box py={2}>
                                    <MUIButton
                                        className="nft-button"
                                        variant="contained"
                                        label="Discover New Revenue Opportunities!"
                                        onClick={openForm}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={4} md={5} className="native-img right">
                            <DisplayImage
                                src={customize}
                                priority={false}
                                alt="Scale Your Earnings with Our White Label Crypto Wallet"
                                width={250}
                            />
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Grid>
            <Grid item xs={12} className="blockchain-revolution-service bottom-spacing top-space mobile-top-padding mobile-bottom-padding">
                <MUIContainer>
                    <Platforms
                        data={dominateDevelopmentService}
                        heading="Dominate the Market with Our White Label Crypto Wallet Development Services"
                        subheading="We offer tailored white label crypto wallet app development services, delivering secure, scalable, and customizable wallet platforms. Our end-to-end solutions, from design to deployment, ensure a seamless user experience, empowering businesses to thrive in the growing digital asset space."
                        containerClass="blockchain-card-container"
                        className="defi-feature-platform-service"
                    />
                </MUIContainer>
            </Grid>
            <Grid item xs={12} className="blockchain-revolution-service  bottom-spacing top-space helper_section_parent mobile-top-padding">
                <MUIContainer>
                    <Platforms
                        data={whiteLabelIndustry}
                        heading="Our Expertise in White Label Crypto Wallet Development Across Industries"
                        subheading="Our white label crypto wallet app is designed to meet the needs of businesses across industries. Whether you're in fintech, retail, or gaming, we’ve got the perfect solution to boost security, streamline transactions, and enhance your user experience—all with zero hassle."
                        containerClass="blockchain-card-container"
                        className="platform-key-industry-service no-border"
                    />
                </MUIContainer>
            </Grid>
            <Grid item xs={12} className="hire-native-team rich-exchange-feature-adv  exchange-banner-white-label launch-defi-wrapper">
                <MUIContainer>
                    <Grid container justifyContent="space-around">
                        <Grid item xs={12} sm={12} md={7} className="defi-short-banner-center">
                            <Box className="spectrum-header-wrapper solution-header-wrapper solutions zero-margin">
                                <Typography className="spectrum-heading defi-adv-heading ">
                                    Looking for a Cryptocurrency Wallet Solution for a Different Vertical?
                                </Typography>

                                <Box py={2}>
                                    <MUIButton
                                        className="nft-button"
                                        variant="contained"
                                        label="Book A Call With Us!"
                                        onClick={() => {
                                            globalThis.open(meetingLink, '_blank');
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={4} md={5} className="native-img right">
                            <DisplayImage
                                src={cryptoIndustry}
                                priority={false}
                                alt="Looking for a Cryptocurrency Wallet Solution for a Different Vertical?"
                                width={300}
                            />
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Grid>
            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MUIContainer>
                    <Platforms
                        heading="Mirror the Success of Leading Crypto Wallets with Our Clones"
                        subheading="Want to launch a wallet like the big players dominating the market? We’ve got your back. Our customized crypto wallet solutions let you roll out a wallet just like the big whales in the game. Check out the top crypto wallets, pick your favorite, and we’ll build it for you."
                        data={whiteLabelClone}
                        className="replicate-success-challange"
                    />
                </MUIContainer>
            </Box>
            <Grid item xs={12} className="hire-native-team rich-exchange-feature-adv  exchange-banner-white-label launch-defi-wrapper">
                <MUIContainer>
                    <Grid container justifyContent="space-around">
                        <Grid item xs={12} sm={12} md={7} className="defi-short-banner-center">
                            <Box className="spectrum-header-wrapper solution-header-wrapper solutions zero-margin">
                                <Typography className="spectrum-heading defi-adv-heading ">
                                    Thinking of Cloning a Wallet or Customizing a White Label Crypto Wallet?
                                </Typography>
                                <Box py={2}>
                                    <MUIButton
                                        className="nft-button"
                                        variant="contained"
                                        label="Share Your Project Requirements!"
                                        onClick={openForm}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={4} md={5} className="native-img right">
                            <DisplayImage
                                priority={false}
                                src={customWalletNiche}
                                alt="Thinking of Cloning a Wallet or Customizing a White Label Crypto Wallet?"
                                width={200}
                            />
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Grid>

            <Box className="bottom-spacing top-space mobile-top-padding  process-mobile-bottom-padding">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black " component="h2">
                            Our Tried-and-True Method for White Label Crypto Wallet Process
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line" />
                        </Box>
                        <Box className="sub-heading-box" pb={4}>
                            <Typography className="spectrum-sub-heading color-black spacing" component="p">
                                We foster white label cryptocurrency wallet development focuses on aligning your business needs with
                                industry standards. We prioritize seamless user experience, top security, and scalability to ensure your
                                wallet is ready for long-term success in the digital space.
                            </Typography>
                        </Box>
                    </Box>
                    <Box textAlign="center">
                        <DisplayImage
                            src={development}
                            priority={false}
                            alt="Our Tried-and-True Method for White Label Crypto Wallet Process"
                            className="approach-main-img approach-web-img"
                        />
                    </Box>

                    <Box className="approach-main-img approach-mobile-img">
                        <MobileProcess mobileProcess={whiteLabelProcess} />
                    </Box>
                </MUIContainer>
            </Box>

            <OurMakeChoice
                heading="What Makes Our"
                subheading="White Label Cryptocurrency Wallet Preferred Choice?"
                body={ourMakeWhiteLabelChoice}
                contentData={makeOurWhiteLabelContent}
            />
            <Grid item xs={12} className="bottom-spacing">
                <LetsDiscuss
                    heading="Ready to Elevate Your Business with Our Custom White Label Crypto Wallet?"
                    btnLabel="Get Your Free Demo Today!"
                    openForm={openForm}
                    className="white-label-sprill"
                />
            </Grid>
            <Grid item xs={12} className="question-parent top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={whiteCryptoFAQ} />
                </MUIContainer>
            </Grid>
            <Grid item xs={12}>
                <Box className="top-space mobile-top-padding bg-blue-background">
                    <Testimonials heading="Client Testimonials" clientData={whiteLabelTestmonialData} />
                </Box>
            </Grid>
            <Grid item xs={12} className="latest-insights top-space mobile-top-padding">
                <LatestInsights id="22" />
            </Grid>
            <script
                type="application/ld+json"
                id="white-label-crypto-exchange-development"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: whiteCryptoFAQ?.map((item: any) => ({
                            '@type': 'Question',
                            name: item.question,
                            acceptedAnswer: {
                                '@type': 'Answer',
                                text: item.answer
                            }
                        }))
                    })
                }}
            />

            <AggregateScript
                name="White Label Crypto Wallet"
                image={`${CDN_URL}?url=%2Fimages%2Fcrypto-images%2Fwhite-label-crypto-wallet.webp&w=750&q=75`}
                description="Elevate your cryptocurrency experience with our White Label Crypto Wallet Solution. Secure, user-friendly, and customizable, this innovative wallet allows you to aggregate and manage multiple cryptocurrencies seamlessly."
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.8',
                    bestRating: '5',
                    worstRating: '1',
                    ratingCount: '326'
                }}
            />

            <script
                id="white-label-video"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'VideoObject',
                        name: 'Carteira - White Label Crypto Wallet Solution',
                        description:
                            'Discover Carteira, your go-to White Label Crypto Wallet Solution for secure digital asset management. Tailored for businesses, Carteira supports multiple cryptocurrencies and offers advanced security features like real-time transaction monitoring and multi-factor authentication. Elevate your crypto business with a customizable, user-friendly wallet solution. Watch the video to learn more!',
                        thumbnailUrl: 'https://img.youtube.com/vi/fcL3BQZwY-s/hqdefault.jpg',
                        uploadDate: '2023-10-31',
                        duration: 'PT1M17S',
                        contentUrl: `${SITE_URL}/white-label-crypto-wallet`,
                        potentialAction: {
                            '@type': 'SeekToAction',
                            target: 'https://youtu.be/fcL3BQZwY-s={seek_to_second_number}',
                            'startOffset-input': 'required name=seek_to_second_number'
                        }
                    })
                }}
            />
        </Box>
    );
};

export default WhiteLabelWallet;
WhiteLabelWallet.Layout = 'Main';
